import { useMutation, UseMutationResult } from 'react-query';
import { api } from '../api';
import queryClient from '../query-client';
import { PostResponseBody } from '../types/api-response';
import { AxiosError } from 'axios';
import { OtherSchedule } from '../types/other-schedule';
import { WorksheetTypeEnum } from '../types/worksheet';
import { useIntercom } from 'react-use-intercom';
import { CustomWorksheetTemplateOverview } from '../types/custom-worksheet-v2';

export type CreateOtherScheduleRequest = (
    | { name: string; description?: string | null; type: WorksheetTypeEnum; tag?: string; priorWorksheetId?: string }
    | { type: WorksheetTypeEnum; calculationTemplate: CustomWorksheetTemplateOverview }
)[];

async function createOtherSchedule(workpaperId: string, req: CreateOtherScheduleRequest): Promise<PostResponseBody<OtherSchedule>> {
    const payload = req.map((r) => {
        if ('calculationTemplate' in r) {
            return {
                type: r.type,
                calculationTemplateId: r.calculationTemplate.id,
            };
        }
        return r;
    });

    const { data } = await api.post(`/workpapers/${workpaperId}/other-schedules`, payload);
    return data;
}

export function useCreateOtherSchedules(
    workpaperId: string
): UseMutationResult<PostResponseBody<OtherSchedule>, AxiosError, CreateOtherScheduleRequest, unknown> {
    const { trackEvent } = useIntercom();
    return useMutation((req) => createOtherSchedule(workpaperId, req), {
        onSettled: () => {
            queryClient.invalidateQueries(['workpapers', workpaperId, 'other-schedules']);
        },
        onSuccess: (_, req) => {
            req.forEach((r) => {
                trackEvent(
                    'other-schedules-create',
                    'calculationTemplate' in r
                        ? {
                              description: 'User has created an other schedule',
                              fromTemplate: true,
                              templateName: r.calculationTemplate.name,
                          }
                        : {
                              description: 'User has created an other schedule',
                              fromTemplate: false,
                          }
                );
            });
        },
    });
}
